// project imports
import { normalizeError } from './normalize-error';

// types
import { IAppError } from './types';

// ==============================|| ERRORS - MODULE-LEVEL BUS ||============================== //
// Lets non-React code (axios interceptors, global window handlers) funnel errors
// to the React ErrorProvider. Buffers until the first subscriber mounts.

type Listener = (error: IAppError) => void;

const listeners = new Set<Listener>();
const buffer: IAppError[] = [];

// re-entrancy guard: never let a listener crash trigger another dispatch loop
let dispatching = false;

export function subscribe(listener: Listener): () => void {
	listeners.add(listener);
	// flush anything captured before the provider mounted
	if (buffer.length) {
		buffer.splice(0).forEach((e) => listener(e));
	}
	return () => {
		listeners.delete(listener);
	};
}

function pushError(error: IAppError): void {
	if (dispatching) return;
	if (listeners.size === 0) {
		buffer.push(error);
		return;
	}
	dispatching = true;
	try {
		listeners.forEach((l) => l(error));
	} finally {
		dispatching = false;
	}
}

// ---- gardă anti-dublă-raportare (pe OBIECTUL de eroare) ----
// Aceeași eroare poate atinge pipeline-ul pe două căi: interceptorul axios o raportează,
// apoi rejection-ul se propagă neprins până la `unhandledrejection`, care ar raporta-o
// din nou. Marcăm obiectul raportat; a doua încercare cu același obiect e ignorată.
const seenRawErrors = new WeakSet<object>();

const isObject = (v: unknown): v is object => typeof v === 'object' && v !== null;

// normalize a raw error (axios error, JS Error, string, …) then dispatch
export function reportError(raw: unknown): void {
	if (isObject(raw)) {
		if (seenRawErrors.has(raw)) return;
		seenRawErrors.add(raw);
	}
	pushError(normalizeError(raw));
}

// Marchează o eroare ca „tratată în alt flux" FĂRĂ să o raporteze — folosit pentru
// excepțiile pipeline-ului: 401 (modala de sesiune expirată, CTZ-2751) și cererile
// anulate (AbortController). Dacă rejection-ul lor ajunge ulterior la
// `unhandledrejection`, garda de mai sus îl ignoră.
export function suppressError(raw: unknown): void {
	if (isObject(raw)) seenRawErrors.add(raw);
}

// ---- golirea log-ului din cod non-React (logout) ----
// Log-ul erorilor e PER SESIUNE de utilizator: AuthProvider îl golește la sign-out
// (același loc unde se golesc și configurările de tabele). Delegat, ca AuthProvider să
// nu depindă de contextul de erori.
let clearLogDelegate: (() => void) | null = null;

export function registerClearErrorLog(clear: () => void): () => void {
	clearLogDelegate = clear;
	return () => {
		if (clearLogDelegate === clear) clearLogDelegate = null;
	};
}

export function clearErrorLog(): void {
	clearLogDelegate?.();
}

// ==============================|| ERRORS - GLOBAL WINDOW HANDLERS ||============================== //
// Installed exactly once per page. The window listeners dispatch through a
// window-stored delegate that each module evaluation refreshes, so HMR remounts
// neither duplicate listeners nor strand them on a stale module instance.

const INSTALL_FLAG = '__cityzenGlobalErrorHandlersInstalled__';
const REPORT_DELEGATE = '__cityzenReportError__';

export function installGlobalErrorHandlers(): void {
	const w = window as unknown as Record<string, unknown>;

	// always point the delegate at THIS module's bus (fresh after HMR)
	w[REPORT_DELEGATE] = reportError;

	if (w[INSTALL_FLAG]) return;
	w[INSTALL_FLAG] = true;

	const dispatch = (raw: unknown): void => {
		const report = w[REPORT_DELEGATE];
		if (typeof report === 'function') (report as (r: unknown) => void)(raw);
	};

	// uncaught runtime errors
	window.addEventListener('error', (event) => {
		// benign browser noise, already suppressed by the app entry point
		if (
			typeof event.message === 'string' &&
			event.message.includes('ResizeObserver loop')
		) {
			return;
		}
		if (event.error || event.message) dispatch(event.error ?? event.message);
	});

	// unhandled promise rejections (incl. axios errors without a catch)
	window.addEventListener('unhandledrejection', (event) => {
		dispatch(event.reason);
	});
}
